import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { medicPortalAPI, shiftsAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { useBranding } from '../../context/BrandingContext';
import { RecentlyViewedCard } from '../../components/RecentlyViewedCard';
import { fmt } from '../../lib/currency';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  CalendarDays, 
  Clock,
  FileText,
  AlertTriangle,
  ChevronRight,
  Send,
  CalendarCheck,
  TrendingUp,
  Eye,
  MapPin
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const STATUS_STYLES = {
  pending: 'bg-[#FFC04D]/15 text-[#FFC04D] border-[#FFC04D]/20',
  confirmed: 'bg-[#1ABC9C]/15 text-[#1ABC9C] border-[#1ABC9C]/20',
  standby: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/20',
  completed: 'bg-gray-500/15 text-gray-400 border-gray-500/20',
  cancelled: 'bg-red-500/15 text-red-400 border-red-500/20',
  compliant: 'bg-[#1ABC9C]/15 text-[#1ABC9C] border-[#1ABC9C]/20',
  expiring_soon: 'bg-[#FFC04D]/15 text-[#FFC04D] border-[#FFC04D]/20',
  expired: 'bg-red-500/15 text-red-400 border-red-500/20',
};

const TYPE_STYLES = {
  regular: 'bg-[#1ABC9C]/15 text-[#1ABC9C]',
  night: 'bg-indigo-500/15 text-indigo-400',
  standby: 'bg-[#FFC04D]/15 text-[#FFC04D]',
  emergency: 'bg-red-500/15 text-red-400',
};

export default function MedicPortal() {
  const { user, isImpersonating } = useAuth();
  const { orgName, hasCustomBrand } = useBranding();
  const navigate = useNavigate();
  const [medicProfile, setMedicProfile] = useState(null);
  const [upcomingShifts, setUpcomingShifts] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [workload, setWorkload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isImpersonationMode, setIsImpersonationMode] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    let profileData = null;
    try {
      const profileRes = await medicPortalAPI.getProfile();
      profileData = profileRes.data;
      setMedicProfile(profileData);
    } catch (error) {
      if (error.response?.status === 403 || error.response?.status === 404) {
        if (isImpersonating) {
          setIsImpersonationMode(true);
        } else {
          toast.error('Failed to load portal data');
        }
      } else {
        toast.error('Failed to load portal data');
      }
      setLoading(false);
      return;
    }

    // Fetch the rest independently so a single failure doesn't hide other widgets
    const [shiftsRes, requestsRes, workloadRes] = await Promise.allSettled([
      medicPortalAPI.getUpcomingShifts(),
      medicPortalAPI.getMyRequests(),
      profileData?.id ? shiftsAPI.workload(profileData.id) : Promise.reject(new Error('no medic id')),
    ]);

    if (shiftsRes.status === 'fulfilled') {
      setUpcomingShifts(shiftsRes.value.data.slice(0, 3));
    }
    if (requestsRes.status === 'fulfilled') {
      setPendingRequests(requestsRes.value.data.filter(r => r.status === 'requested'));
    }
    if (workloadRes.status === 'fulfilled') {
      setWorkload(workloadRes.value.data);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-[#1ABC9C]/20 border-t-[#1ABC9C] rounded-full animate-spin" />
      </div>
    );
  }

  // Show impersonation preview mode
  if (isImpersonationMode) {
    return (
      <div className="space-y-6" data-testid="medic-portal-impersonation">
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-lg bg-amber-500/20">
              <Eye className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-amber-200 font-['Manrope']">
                Medic Portal Preview Mode
              </h2>
              <p className="text-amber-300/80 mt-2">
                You are viewing the Medic Portal as an admin impersonating a medic role. 
                Since your admin account does not have an associated medic profile, 
                some features are limited.
              </p>
              <div className="mt-4 text-sm text-amber-400/70">
                <p className="font-medium mb-2">What you can test:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Navigation structure and layout</li>
                  <li>UI components and styling</li>
                  <li>Permission-based feature visibility</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Sample Portal Layout Preview */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-[#2B2B2B] border-white/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-[#1ABC9C]/15">
                  <CalendarCheck className="w-5 h-5 text-[#1ABC9C]" />
                </div>
                <div>
                  <p className="text-sm text-[#A1A1AA]">Upcoming Shifts</p>
                  <p className="text-xl font-bold text-[#F2F2F2]/50 font-['JetBrains_Mono']">--</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#2B2B2B] border-white/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-[#FFC04D]/15">
                  <Clock className="w-5 h-5 text-[#FFC04D]" />
                </div>
                <div>
                  <p className="text-sm text-[#A1A1AA]">Pending Requests</p>
                  <p className="text-xl font-bold text-[#F2F2F2]/50 font-['JetBrains_Mono']">--</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#2B2B2B] border-white/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-indigo-500/15">
                  <TrendingUp className="w-5 h-5 text-indigo-400" />
                </div>
                <div>
                  <p className="text-sm text-[#A1A1AA]">Est. Earnings</p>
                  <p className="text-xl font-bold text-[#F2F2F2]/50 font-['JetBrains_Mono']">{fmt(0, user?.organisation_currency, { decimals: 0 }).replace('0', '--')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#2B2B2B] border-white/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-[#1ABC9C]/15">
                  <FileText className="w-5 h-5 text-[#1ABC9C]" />
                </div>
                <div>
                  <p className="text-sm text-[#A1A1AA]">Documents</p>
                  <p className="text-xl font-bold text-[#F2F2F2]/50 font-['JetBrains_Mono']">--</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="bg-[#2B2B2B] border-white/5">
          <CardHeader>
            <CardTitle className="text-[#F2F2F2] font-['Manrope']">Portal Navigation</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                { name: 'My Shifts', path: '/portal/shifts', icon: CalendarDays },
                { name: 'Open Shifts', path: '/portal/open-shifts', icon: Send },
                { name: 'Availability', path: '/portal/availability', icon: Clock },
                { name: 'My Expenses', path: '/portal/expenses', icon: TrendingUp },
                { name: 'Documents', path: '/portal/documents', icon: FileText },
              ].map((item) => (
                <Button
                  key={item.path}
                  variant="outline"
                  className="border-white/10 text-[#F2F2F2] justify-start"
                  onClick={() => navigate(item.path)}
                >
                  <item.icon className="w-4 h-4 mr-2" />
                  {item.name}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Calculate stats — iter64. rate_payable is per-hour; multiply by
  // scheduled hours. Mirrors backend's _shift_hours().
  const shiftHours = (shift) => {
    try {
      const [sh, sm] = (shift.start_time || '0:0').split(':').map(Number);
      const [eh, em] = (shift.end_time || '0:0').split(':').map(Number);
      let start = sh * 60 + sm;
      let end = eh * 60 + em;
      if (end <= start) end += 24 * 60;
      return (end - start) / 60;
    } catch { return 0; }
  };
  const totalUpcomingEarnings = upcomingShifts.reduce(
    (sum, s) => sum + (Number(s.rate_payable) || 0) * shiftHours(s), 0,
  );
  const expiringDocs = medicProfile?.compliance_details?.expiring_soon_docs || [];
  const expiredDocs = medicProfile?.compliance_details?.expired_docs || [];

  return (
    <div className="space-y-6" data-testid="medic-portal-container">
      {/* iter72.6 — recents card above the welcome banner so a half-
          completed shift is one tap away for the medic too. */}
      <RecentlyViewedCard />
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-[#1ABC9C]/20 to-[#1ABC9C]/5 rounded-xl p-6 border border-[#1ABC9C]/20">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-[#F2F2F2] font-['Manrope']" data-testid="medic-welcome-heading">
              Welcome back, {user?.name}!
            </h2>
            {hasCustomBrand && (
              <p className="text-sm text-[#A1A1AA] mt-0.5" data-testid="medic-welcome-org">
                Signed in to <span className="text-white font-medium">{orgName}</span>
              </p>
            )}
            <div className="text-[#A1A1AA] mt-1 flex items-center gap-2">
              <span>Compliance status:</span>
              <Badge className={STATUS_STYLES[medicProfile?.compliance_status] || STATUS_STYLES.pending}>
                {medicProfile?.compliance_status || 'pending'}
              </Badge>
            </div>
          </div>
          <Button 
            className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90"
            onClick={() => navigate('/portal/open-shifts')}
          >
            <Send className="w-4 h-4 mr-2" />
            Find Open Shifts
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#1ABC9C]/15">
                <CalendarCheck className="w-5 h-5 text-[#1ABC9C]" />
              </div>
              <div>
                <p className="text-sm text-[#A1A1AA]">Upcoming Shifts</p>
                <p className="text-xl font-bold text-[#F2F2F2] font-['JetBrains_Mono']">
                  {upcomingShifts.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#FFC04D]/15">
                <Clock className="w-5 h-5 text-[#FFC04D]" />
              </div>
              <div>
                <p className="text-sm text-[#A1A1AA]">Pending Requests</p>
                <p className="text-xl font-bold text-[#F2F2F2] font-['JetBrains_Mono']">
                  {pendingRequests.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#1ABC9C]/15">
                <TrendingUp className="w-5 h-5 text-[#1ABC9C]" />
              </div>
              <div>
                <p className="text-sm text-[#A1A1AA]">Upcoming Earnings</p>
                <p className="text-xl font-bold text-[#1ABC9C] font-['JetBrains_Mono']">
                  {fmt(totalUpcomingEarnings, user?.organisation_currency, { decimals: 0 })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${(expiringDocs.length + expiredDocs.length) > 0 ? 'bg-red-500/15' : 'bg-[#1ABC9C]/15'}`}>
                <FileText className={`w-5 h-5 ${(expiringDocs.length + expiredDocs.length) > 0 ? 'text-red-400' : 'text-[#1ABC9C]'}`} />
              </div>
              <div>
                <p className="text-sm text-[#A1A1AA]">Doc Alerts</p>
                <p className="text-xl font-bold text-[#F2F2F2] font-['JetBrains_Mono']">
                  {expiringDocs.length + expiredDocs.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* This Week Workload */}
      {workload && (
        <Card className="bg-[#2B2B2B] border-white/5" data-testid="medic-workload-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${workload.over_limit ? 'bg-red-500/15' : 'bg-[#1ABC9C]/15'}`}>
                  <Clock className={`w-5 h-5 ${workload.over_limit ? 'text-red-400' : 'text-[#1ABC9C]'}`} />
                </div>
                <div>
                  <p className="text-sm text-[#A1A1AA]">This week ({workload.week_start} – {workload.week_end})</p>
                  <p className="text-xl font-bold text-[#F2F2F2] font-['JetBrains_Mono']">
                    <span data-testid="medic-workload-hours">{workload.total_hours}</span>
                    <span className="text-sm text-[#A1A1AA] font-normal"> / {workload.max_weekly_hours}h · {workload.shift_count} shift{workload.shift_count === 1 ? '' : 's'}</span>
                  </p>
                </div>
              </div>
              <div className="min-w-[160px] flex-1 max-w-md">
                <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                  <div
                    className={`h-full ${workload.over_limit ? 'bg-red-400' : 'bg-[#1ABC9C]'}`}
                    style={{ width: `${Math.min(100, (workload.total_hours / Math.max(1, workload.max_weekly_hours)) * 100)}%` }}
                  />
                </div>
                {workload.over_limit && (
                  <p className="text-xs text-red-400 mt-1">You're over the weekly cap — please contact ops before booking more shifts.</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Alerts */}
      {(expiredDocs.length > 0 || expiringDocs.length > 0) && (
        <Card className={`${expiredDocs.length > 0 ? 'bg-red-500/10 border-red-500/20' : 'bg-[#FFC04D]/10 border-[#FFC04D]/20'}`}>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className={`w-5 h-5 mt-0.5 ${expiredDocs.length > 0 ? 'text-red-400' : 'text-[#FFC04D]'}`} />
              <div className="flex-1">
                <p className={`font-medium ${expiredDocs.length > 0 ? 'text-red-400' : 'text-[#FFC04D]'}`}>
                  {expiredDocs.length > 0 ? 'Documents Expired' : 'Documents Expiring Soon'}
                </p>
                <p className="text-sm text-[#A1A1AA] mt-1">
                  {expiredDocs.length > 0 
                    ? `You have ${expiredDocs.length} expired document(s). Please update immediately to maintain your active status.`
                    : `You have ${expiringDocs.length} document(s) expiring in the next 30 days. Please update them soon.`
                  }
                </p>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                className={`border-current ${expiredDocs.length > 0 ? 'text-red-400 hover:bg-red-400/10' : 'text-[#FFC04D] hover:bg-[#FFC04D]/10'}`}
                onClick={() => navigate('/portal/documents')}
              >
                View Documents
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Shifts */}
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-[#F2F2F2] font-['Manrope'] flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-[#1ABC9C]" />
              Upcoming Shifts
            </CardTitle>
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-[#1ABC9C]"
              onClick={() => navigate('/portal/shifts')}
            >
              View All
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {upcomingShifts.length === 0 ? (
              <div className="text-center py-8 text-[#A1A1AA]">
                <CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No upcoming shifts</p>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="mt-3 border-[#1ABC9C]/30 text-[#1ABC9C]"
                  onClick={() => navigate('/portal/open-shifts')}
                >
                  Browse Open Shifts
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {upcomingShifts.map(shift => {
                  const today = format(new Date(), 'yyyy-MM-dd');
                  const canCheckIn =
                    (shift.date || '').substring(0, 10) === today &&
                    !shift.checked_in_at &&
                    !shift.checked_out_at &&
                    !['cancelled', 'completed'].includes(shift.status);
                  return (
                  <div 
                    key={shift.id}
                    className="p-4 bg-[#1A1A1A] rounded-lg border border-white/5 hover:border-[#1ABC9C]/20 transition-colors cursor-pointer group"
                    onClick={() => navigate(`/portal/shifts/${shift.id}`)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Badge className={STATUS_STYLES[shift.status]}>
                            {shift.status}
                          </Badge>
                          <Badge className={`${TYPE_STYLES[shift.shift_type]} border-0`}>
                            {shift.shift_type}
                          </Badge>
                          {shift.checked_in_at && !shift.checked_out_at && (
                            <Badge className="bg-[#1ABC9C]/15 text-[#1ABC9C] border-[#1ABC9C]/20">
                              In progress
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                          <Clock className="w-4 h-4" />
                          <span>{format(new Date(shift.date), 'EEE, MMM dd')} | {shift.start_time} - {shift.end_time}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                          <MapPin className="w-4 h-4" />
                          <span>{shift.location}</span>
                        </div>
                        {canCheckIn && (
                          <Button
                            size="sm"
                            className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90 text-white mt-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/portal/shifts/${shift.id}#checkin`);
                            }}
                            data-testid={`dashboard-checkin-btn-${shift.id}`}
                          >
                            <MapPin className="w-4 h-4 mr-1" />
                            Check In
                          </Button>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[#1ABC9C] font-bold font-['JetBrains_Mono']">
                          {fmt(shift.rate_payable, shift.currency || 'GBP', { decimals: 0 })}
                        </span>
                        <ChevronRight className="w-4 h-4 text-[#A1A1AA] group-hover:text-[#1ABC9C]" />
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pending Requests */}
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-[#F2F2F2] font-['Manrope'] flex items-center gap-2">
              <Send className="w-5 h-5 text-[#FFC04D]" />
              Pending Requests
            </CardTitle>
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-[#1ABC9C]"
              onClick={() => navigate('/portal/open-shifts')}
            >
              View All
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {pendingRequests.length === 0 ? (
              <div className="text-center py-8 text-[#A1A1AA]">
                <Send className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No pending requests</p>
                <p className="text-sm mt-1">Request open shifts to get assigned</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pendingRequests.slice(0, 3).map(request => (
                  <div 
                    key={request.id}
                    className="p-4 bg-[#1A1A1A] rounded-lg border border-white/5"
                  >
                    <div className="flex items-start justify-between">
                      <div className="space-y-2">
                        <Badge className={STATUS_STYLES.pending}>
                          <Clock className="w-3 h-3 mr-1" />
                          Awaiting Approval
                        </Badge>
                        <div className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                          <CalendarDays className="w-4 h-4" />
                          <span>
                            {request.shift_date 
                              ? format(new Date(request.shift_date), 'EEE, MMM dd')
                              : 'N/A'
                            }
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                          <MapPin className="w-4 h-4" />
                          <span>{request.shift_location || 'N/A'}</span>
                        </div>
                      </div>
                      <div className="text-right text-xs text-[#A1A1AA]">
                        <p>Requested</p>
                        <p>{format(new Date(request.created_at), 'MMM dd')}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
